import { toDateOnly } from "./dateOnly";

/**
 * Repair-tracking date arithmetic for a listing's IN REPAIR details.
 *
 * Everything here works on `YYYY-MM-DD` strings, never on `Date` objects built
 * from them. Parsing "2026-09-01" with `new Date()` yields UTC midnight, which
 * is the PREVIOUS day for anyone west of Greenwich — the same trap
 * core/dateOnly.js documents, and it would report a repair as a day younger (or
 * a day overdue) depending on where the rep happens to sit.
 *
 * Day counts are therefore differences between UTC-normalised calendar dates:
 * whole days, no DST discontinuity, no hour-of-day sensitivity.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** "YYYY-MM-DD" for the viewer's own calendar day. */
export const todayDateOnly = (now = new Date()) => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** A date-only string, or null for anything that is not one. */
const asDateOnly = (value) => {
  const trimmed = toDateOnly(value);
  if (typeof trimmed !== "string") return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null;
};

const utcMs = (dateOnly) => {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

/** Whole calendar days between two date-only strings. Null if either is unusable. */
export const daysBetween = (from, to) => {
  const a = asDateOnly(from);
  const b = asDateOnly(to);
  if (!a || !b) return null;
  return Math.round((utcMs(b) - utcMs(a)) / MS_PER_DAY);
};

/**
 * How long the watch has been away, as of `today`. Null when the hand-over date
 * is unrecorded; clamped at 0 for a date typed in the future, which is a typo
 * rather than a watch that has been in repair for minus three days.
 */
export const daysInRepair = (startedAt, today = todayDateOnly()) => {
  const days = daysBetween(startedAt, today);
  if (days === null) return null;
  return days < 0 ? 0 : days;
};

/** Is `today` past the expected return date? False when no date is set. */
export const isRepairOverdue = (expectedReturnAt, today = todayDateOnly()) => {
  const due = asDateOnly(expectedReturnAt);
  const now = asDateOnly(today);
  if (!due || !now) return false;
  // ISO date-only strings sort lexicographically, so this needs no parsing.
  return now > due;
};

const formatDisplayDate = (dateOnly) => {
  const [y, m, d] = dateOnly.split("-");
  // House format: en-GB, dd/MM/yyyy (see components/formatters/Date.jsx).
  return `${d}/${m}/${y}`;
};

/**
 * The one line shown under the repair dates.
 *
 * Once the expected return date has passed, the running day count is
 * DELIBERATELY not shown (Alex, 2026-08-25): at that point the number is no
 * longer the useful fact — "this is late" is — and a counter that keeps ticking
 * up reads as a normal status rather than a problem. An overdue repair still
 * says so, because rendering nothing at all looks like a broken field.
 *
 * @returns {{days: number|null, overdue: boolean, text: string|null}}
 */
export const repairAgeSummary = ({
  startedAt,
  expectedReturnAt,
  today = todayDateOnly(),
} = {}) => {
  const overdue = isRepairOverdue(expectedReturnAt, today);
  const days = daysInRepair(startedAt, today);

  if (overdue) {
    return {
      days,
      overdue: true,
      text: `Overdue — was expected back ${formatDisplayDate(asDateOnly(expectedReturnAt))}`,
    };
  }

  if (days === null) {
    return { days: null, overdue: false, text: null };
  }

  if (days === 0) {
    return { days: 0, overdue: false, text: "Given for repair today" };
  }

  return {
    days,
    overdue: false,
    text: `${days} ${days === 1 ? "day" : "days"} in repair`,
  };
};
